import {LangType} from 'yougile-lang'
import {ObjUtil, RelativeTime, Util} from 'yougile-util'
import {createDataObjectId, DataObjectId} from './data-object'
import {Hub} from './hubs'
import {ReportFieldKey} from './report-data-field'
import {Schedule} from './schedule'

export const reportsContainerId =
  '00000000-60ff-4483-bb16-6b137e980206' as DataObjectId

export interface Report {
  id: DataObjectId
  title: string
  columns: ReportColumn[]
  availableTo: DataObjectId[]
  aggregatedForUsers?: DataObjectId[]
  schedule?: ReportSchedule
  isExample?: boolean
}

export const locationFilterSubtaskLevels = [
  'level0', // tasks on board
  'level1', // direct subtasks of tasks on board
  'other' // deeper descendants of tasks on board
] as const

export type LocationFilterSubtaskLevel =
  typeof locationFilterSubtaskLevels[number]

export interface LocationFilterItem {
  parent: DataObjectId
  subtasks: {
    [key in LocationFilterSubtaskLevel]?: boolean
  }
}

export const locationFilterSubtasksToArray = (
  subtasks: LocationFilterItem['subtasks'] | undefined
): LocationFilterSubtaskLevel[] => {
  if (!subtasks) {
    return []
  }
  const res: LocationFilterSubtaskLevel[] = []
  if (subtasks.level0) {
    res.push('level0')
  }
  if (subtasks.level1) {
    res.push('level1')
  }
  if (subtasks.other) {
    res.push('other')
  }
  return res
}

export const locationFilterArrayToSubtasks = (
  arr: LocationFilterSubtaskLevel[] | undefined
): LocationFilterItem['subtasks'] => {
  if (!arr) {
    return {level0: true}
  }
  return {
    level0: arr.includes('level0'),
    level1: arr.includes('level1'),
    other: arr.includes('other')
  }
}

export const fixOldReportColumn = (report: ReportColumn): ReportColumn => {
  if (report?.filter?.childOf) {
    const result = Util.clone(report)
    result.filter.located ??= []
    for (const id of result?.filter?.childOf ?? []) {
      result.filter.located.push({parent: id, subtasks: {level0: true}})
    }
    result.filter.childOf = undefined
    if (result.activeFilters) {
      const activeFilters = new Set(result.activeFilters)
      const hasLocated =
        activeFilters.has('childOf') || activeFilters.has('located')
      activeFilters.delete('childOf')
      if (hasLocated) {
        activeFilters.add('located')
      }
      result.activeFilters = [...activeFilters]
    }
    return result
  } else {
    return report
  }
}

export interface ReportColumn {
  title: string
  sort?: ReportSort[]
  activeFilters?: Array<keyof ReportColumn['filter']>
  filter: {
    located?: LocationFilterItem[]
    isCompleted?: ReportBooleanFilter
    isArchived?: ReportBooleanFilter

    createdBy?: ReportUserFilter[]
    assignedBy?: ReportUserFilter[]
    assignedTo?: ReportUserFilter[]
    responsible?: ReportUserFilter[]
    stickers?: ReportStickerFilter
    createdAt?: ReportDateFilter
    movedAt?: ReportDateFilter
    lastMessageAt?: ReportDateFilter
    deadline?: ReportDateFilter
    completedAt?: ReportDateFilter
    timetracking?: ReportTimetrackingFilter
    timer?: ReportTimerFilter
    stopwatch?: ReportTimerFilter

    text?: ReportTextFilter
    chat?: ReportChatFilter

    subtasks?: ReportBooleanFilter
    subtasksDeadline?: ReportDateFilter
    subtasksAssigned?: ReportUserFilter[]
    subtasksCompleted?: ReportBooleanFilter

    /**
     * @deprecated
     * for compatibility with old clients
     * (`located` filter is used instead)
     */
    childOf?: DataObjectId[]
  }
  fields?: ReportFieldKey[]
}

export interface ReportSchedule {
  enabled: boolean
  emails: string[]
  time: Schedule
}

export interface ReportSort {
  key: ReportFieldKey
  asc: boolean
}

export type ReportTextFilterPlace =
  | 'task-title'
  | 'column-title'
  | 'board-title'
  | 'project-title'
  | 'task-description'
  | 'checklists'
  | 'filenames'
  | 'messages'

export interface ReportTextFilter {
  places: ReportTextFilterPlace[]
  text: string
}

export interface ReportChatFilter {
  type: 'no-messages' | 'more' | 'less'
  count?: number
}

export interface ReportUserFilter {
  user: 'not-set' | DataObjectId
  negate?: boolean
}

export interface ReportDateFilter {
  type?: 'expired' | 'not-expired' | 'set' | 'not-set'
  start: RelativeTime
  end: RelativeTime
  custom?: boolean
  datesFrom: number | 'now'
}

export interface ReportTimetrackingFilter {
  plan?: 'not-set' | 'set'
  work?: 'not-set' | 'set' | '>plan' | '<=plan'
}

interface ReportStickerValueFilter {
  values: Array<{id: string | 'not-set' | 'not-pinned'; exclude?: boolean}>
}

export interface ReportStickerFilter {
  [stickerId: string]: ReportStickerValueFilter
}

export type ReportStickerRangePrefixType = 'from-' | 'to-'

export const ReportStickerRangePrefix: Record<
  string,
  ReportStickerRangePrefixType
> = {
  from: 'from-',
  to: 'to-'
}

export const getRangeValue = (
  values: string[],
  prefix: ReportStickerRangePrefixType
): number | undefined => {
  const value = values.find(value => value.startsWith(prefix))?.split(prefix)[1]

  return value ? Number(value) : undefined
}

export interface ReportTimerFilter {
  type?:
    | 'not-pinned'
    | 'pinned'
    | 'not-set'
    | 'set'
    | 'not-expired'
    | 'expired'
    | 'stopped'
}

export interface ReportBooleanFilter {
  type: 'not-set' | 'value'
  value?: boolean
}

export const createReportHub = (companyId: DataObjectId, report: Report) => {
  const hub = new Hub(companyId, report.id)
  hub.id = report.id
  hub.data.report = report
  return hub
}

export const createEmptyReport = (availableTo: DataObjectId[]): Report => ({
  id: Util.UUID() as DataObjectId,
  title: '',
  columns: [createEmptyReportColumn()],
  availableTo
})

export const createEmptyReportColumn = (): ReportColumn => ({
  title: '',
  filter: {}
})

export const isEmptyReportColumn = (reportColumn: ReportColumn) =>
  ObjUtil.isEmpty(reportColumn.filter)

export const isEmptyReport = (report: Report) =>
  report.columns.every(isEmptyReportColumn)

export const identitySchedule = (
  schedule?: ReportSchedule
): ReportSchedule => ({
  enabled: schedule ? schedule.enabled : false,
  emails: schedule && schedule.emails.length > 0 ? schedule.emails : [''],
  time: schedule?.time?.type
    ? schedule.time
    : {type: 'weekly', hour: 18, minute: 0, daysOfWeek: [5]}
})

export const createDeafultAggregatedReport = (
  lang: LangType,
  userId: DataObjectId
): Report => {
  const columnOpenTasks: ReportColumn = {
    title: lang.report.openTasks,
    activeFilters: ['isCompleted'],
    filter: {
      isCompleted: {type: 'value', value: false}
    },
    sort: [{key: 'createdAt', asc: false}]
  }
  const columnCompletedTasks: ReportColumn = {
    title: lang.report.completedTasks,
    activeFilters: ['isCompleted'],
    filter: {
      isCompleted: {type: 'value', value: true}
    },
    sort: [{key: 'createdAt', asc: false}]
  }
  return {
    id: createDataObjectId(),
    title: '',
    availableTo: [],
    columns: [columnOpenTasks, columnCompletedTasks],
    aggregatedForUsers: [userId]
  }
}

export const createDefaultReports = (
  lang: LangType,
  userId: DataObjectId
): Report[] => {
  const makeRep = (title: string, col: ReportColumn): Report => ({
    id: createDataObjectId(),
    availableTo: [userId],
    isExample: true,
    title,
    columns: [col]
  })
  const last30d: ReportDateFilter = {
    start: {unit: 'day', value: -30},
    end: {unit: 'day', value: 1},
    datesFrom: 'now'
  }
  const new30d = makeRep(lang.report.exampleNew30d, {
    title: '',
    filter: {createdAt: last30d},
    fields: [
      'id-task',
      'title',
      'createdAt',
      'createdBy',
      'assignedTo',
      'deadline',
      'completedAt'
    ],
    sort: [{key: 'createdAt', asc: false}]
  })
  const compl30d = makeRep(lang.report.exampleCompleted30d, {
    title: '',
    filter: {completedAt: last30d},
    fields: [
      'id-task',
      'title',
      'createdAt',
      'createdBy',
      'assignedTo',
      'deadline',
      'completedAt'
    ],
    sort: [{key: 'completedAt', asc: false}]
  })
  const byProject = makeRep(lang.report.exampleByProjects, {
    title: '',
    filter: {},
    fields: [
      'id-task',
      'title',
      'projectField',
      'createdAt',
      'createdBy',
      'assignedTo',
      'deadline',
      'completedAt'
    ],
    sort: [
      {key: 'createdAt', asc: false},
      {key: 'projectField', asc: true}
    ]
  })
  const activeChats = makeRep(lang.report.exampleWithChats, {
    title: '',
    filter: {lastMessageAt: last30d},
    fields: [
      'id-task',
      'title',
      'lastMessage',
      'createdAt',
      'createdBy',
      'assignedTo',
      'deadline',
      'completedAt'
    ],
    sort: [{key: 'lastMessage', asc: false}]
  })
  return [new30d, compl30d, byProject, activeChats]
}
